import React from 'react';
import { SecondaryButton, DangerButton } from '../../../ui/common';
import { Modal } from '../../../components/ui/Modal';
import { Banner } from '../../../components/ui/Banner';
import { DiscountField, AmountNote } from '../../../ui/AmountField';
import { useTranslation } from '../../../translations';
import { formatMoney } from '../../../utils/format';
import { amountErrorMessage } from '../../../utils/amountField';
import { computeCancellationRefund, sanitizeFeeInput } from '../utils';
import { nazwaMetody } from '../../../utils/metodaWplaty';

const blok = {
  marginBottom: 16, padding: 12, borderRadius: 'var(--r-md)',
  background: 'var(--info-bg)', border: '1px solid var(--info-border)',
  fontSize: 'var(--fs-base)',
};
const wiersz = { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 };
const podpis = { color: 'var(--text-muted)' };
const drobny = { display: 'block', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' };

/**
 * Okno anulowania rezerwacji.
 *
 * Od 12.10.2026 (test odbiorczy płatności) okno pokazuje WSZYSTKIE pieniądze przy rezerwacji
 * (`money` z `utils.js#cancellationMoney`), a nie tylko płatności online do zwrotu i tylko
 * administratorowi: wpłaty gotówką i przelewem (oddaje się je ręcznie), kaucję zablokowaną na karcie
 * (administrator zwalnia ją razem z anulowaniem) i kaucję pobraną. Pracownik bez uprawnień
 * administratora nie może zwrócić wpłaty ani zwolnić kaucji, więc rezerwacji z przyjętymi pieniędzmi
 * nie anuluje: dostaje informację, co przyjęto i że anuluje administrator (serwer i tak odpowiada wtedy
 * 403 `cancel_paid_admin_required`).
 *
 * Opłata za anulowanie to pole kwoty z jednostką jak rabat (`ui/AmountField.js#DiscountField`):
 * startuje PUSTE (dawne „0” z kursorem przed zerem zamieniało „50” w „500”), a opłata ponad wpłatę
 * nie jest już po cichu przycinana, tylko blokuje anulowanie z komunikatem pod polem.
 */
const DeleteConfirmationModal = ({
  reservation, onConfirm, onCancel, loading, error,
  money = null, isAdmin = true,
  refundableLoading = false, refundableLoadFailed = false,
  feeRaw = '', feeUnit = 'PLN', feeLocked = false, onFeeChange, onUnitChange,
  releaseDeposit = true, onReleaseDepositChange,
}) => {
  const { t, currentLanguage } = useTranslation();
  const zl = (grosze) => formatMoney(grosze / 100, { locale: currentLanguage });
  const m = money || {
    refundable: [], onlineGrosze: 0, offlineGrosze: 0, offlineMethods: [],
    depositsAuthorized: [], depositAuthorizedGrosze: 0, depositCapturedGrosze: 0,
    invoicePayments: [], invoiceGrosze: 0, anyMoney: false,
  };
  const refundable = m.refundable || [];
  // P3.16: opłata za anulowanie — zatrzymujesz część, reszta wraca klientowi.
  const { totalPaidGrosze, refundGrosze, feeError } = computeCancellationRefund(refundable, feeRaw, feeUnit);
  const feeMessage = feeError === 'amountOver'
    ? t('calendar.delete.feeOverPaid', { amount: zl(totalPaidGrosze) })
    : amountErrorMessage(feeError, totalPaidGrosze / 100, { t, locale: currentLanguage });
  // Pracownik nie rozliczy pieniędzy (zwrot i zwolnienie kaucji są dla administratora).
  const tylkoAdministrator = !isAdmin && m.anyMoney && !refundableLoadFailed;
  const blokada = loading || refundableLoading || tylkoAdministrator || (isAdmin && refundable.length > 0 && !!feeError);
  const metodyNaMiejscu = (m.offlineMethods || []).map((x) => nazwaMetody(x, t)).filter(Boolean).join(', ');

  let etykieta;
  if (refundableLoading) etykieta = t('calendar.delete.checkingPayments');
  else if (loading) etykieta = t('calendar.delete.deleting');
  else if (refundableLoadFailed) etykieta = t('calendar.delete.deleteAnyway');
  else if (isAdmin && refundable.length > 0 && feeError) etykieta = t('calendar.delete.fixFee');
  else if (isAdmin && refundGrosze > 0) etykieta = t('calendar.delete.refundAndDelete', { amount: zl(refundGrosze) });
  else etykieta = t('calendar.delete.confirmCancel');

  return (
    <Modal
      open
      /* Okno otwiera się Z okna szczegółów rezerwacji (FormModal, --z-modal), więc musi stać
         nad nim; bez tego pokazywało się pod spodem i wyglądało, jakby „Usuń" nie działało. */
      elevated
      onClose={onCancel}
      dismissOnBackdrop={!loading}
      title={t('calendar.delete.title')}
      size="sm"
      footer={
        <>
          {/* „Nie anuluj", a nie „Anuluj": obok stoi „Anuluj rezerwację" (2026-10-02 przycisk anuluje
              zamiast kasować), a dwa „Anuluj" obok siebie znaczyłyby dwie przeciwne rzeczy. */}
          <SecondaryButton onClick={onCancel} disabled={loading} data-cy="reservation-cancel-keep">
            {t('calendar.delete.keep')}
          </SecondaryButton>
          <DangerButton
            data-cy="reservation-cancel-confirm"
            onClick={onConfirm}
            disabled={blokada}
            style={{ opacity: blokada && !loading ? 0.7 : 1 }}
          >
            {/* code-review #1: dopóki nie wiemy, czy są opłacone płatności, NIE pozwalamy usunąć
                (inaczej pusta lista = „Usuń" i klik skasowałby rezerwację bez zwrotu). */}
            {etykieta}
          </DangerButton>
        </>
      }
    >
      <p style={{ marginTop: 0 }}>{t('calendar.delete.confirmMessage', { id: reservation.id })}</p>
      <p style={{ color: 'var(--danger)', fontWeight: 600 }}>{t('calendar.delete.irreversible')}</p>

      {/* Pracownik: rezerwacja z przyjętymi pieniędzmi. Kwoty widać, ale anuluje administrator. */}
      {tylkoAdministrator && (
        <div style={{ marginBottom: 16 }} data-cy="reservation-cancel-admin-required">
          <Banner tone="warning" compact>{t('calendar.delete.adminRequired')}</Banner>
        </div>
      )}

      {/* P3.16: rezerwacja ma opłaconą płatność online, wpisz opłatę do zatrzymania, resztę oddajemy. */}
      {refundable.length > 0 && (
        <div style={blok} data-cy="reservation-cancel-online">
          <div style={wiersz}>
            <span style={podpis}>{t('calendar.delete.paidOnline')}</span>
            <strong>{zl(isAdmin ? totalPaidGrosze : m.onlineGrosze)}</strong>
          </div>
          {isAdmin && (
            <>
              <label htmlFor="cancel-fee" style={{ display: 'block', marginBottom: 6 }}>
                {t('calendar.delete.cancelFeeLabel')}
              </label>
              <DiscountField
                id="cancel-fee"
                name="cancelFee"
                value={feeRaw}
                unit={feeUnit}
                // Akceptuj cyfry + JEDEN separator (kropka lub przecinek); helper normalizuje ',' → '.'.
                onChange={(v) => onFeeChange && onFeeChange(sanitizeFeeInput(v))}
                onUnitChange={(u) => onUnitChange && onUnitChange(u)}
                placeholder="0"
                invalid={!!feeError}
                disabled={loading || feeLocked}
                ariaLabel={t('calendar.delete.cancelFeeAria')}
                unitGroupLabel={t('calendar.delete.cancelFeeUnitAria')}
                describedBy="cancel-fee-note"
                dataCy="reservation-cancel-fee"
              />
              <AmountNote id="cancel-fee-note" data-error={!!feeError} role={feeError ? 'alert' : undefined} data-cy="reservation-cancel-fee-note">
                {feeMessage || ''}
              </AmountNote>
              <div style={{ ...wiersz, marginBottom: 0, paddingTop: 8, borderTop: '1px solid var(--info-border)' }}>
                <span style={podpis}>{t('calendar.delete.toRefund')}</span>
                <strong data-cy="reservation-cancel-to-refund" style={{ color: !feeError && refundGrosze > 0 ? 'var(--success)' : 'var(--text-muted)' }}>
                  {feeError ? '—' : zl(refundGrosze)}
                </strong>
              </div>
              <span style={{ ...drobny, marginTop: 8 }}>
                {feeLocked ? t('calendar.delete.feeLockedHint') : t('calendar.delete.feeHint')}
              </span>
            </>
          )}
        </div>
      )}

      {/* Wpłaty przyjęte na miejscu (gotówka, przelew): panel ich nie zwróci, robi to obsługa. */}
      {m.offlineGrosze > 0 && (
        <div style={blok} data-cy="reservation-cancel-offline">
          <div style={wiersz}>
            <span style={podpis}>
              {metodyNaMiejscu ? t('calendar.delete.paidOnSiteWithMethods', { methods: metodyNaMiejscu }) : t('calendar.delete.paidOnSite')}
            </span>
            <strong>{zl(m.offlineGrosze)}</strong>
          </div>
          {isAdmin && <span style={drobny}>{t('calendar.delete.paidOnSiteHint')}</span>}
        </div>
      )}

      {/* Wpłaty wpisane ręcznie na fakturach rezerwacji (proforma i przelew): rejestr wpłat ich nie zna,
          więc panel ich nie zwróci. Po fakturze, bo ten sam przelew bywa wpisany na dwóch dokumentach. */}
      {m.invoiceGrosze > 0 && (
        <div style={blok} data-cy="reservation-cancel-invoices">
          {(m.invoicePayments || []).map((f, i) => (
            <div style={wiersz} key={`${f.number}-${i}`}>
              <span style={podpis}>{t('calendar.delete.paidOnInvoice', { number: f.number })}</span>
              <strong>{zl(f.grosze)}</strong>
            </div>
          ))}
          {isAdmin && <span style={drobny}>{t('calendar.delete.paidOnInvoiceHint')}</span>}
        </div>
      )}

      {/* Kaucja zablokowana na karcie: administrator zwalnia ją razem z anulowaniem (domyślnie tak). */}
      {m.depositAuthorizedGrosze > 0 && (
        <div style={blok} data-cy="reservation-cancel-deposit">
          <div style={wiersz}>
            <span style={podpis}>{t('calendar.delete.depositAuthorized')}</span>
            <strong>{zl(m.depositAuthorizedGrosze)}</strong>
          </div>
          {isAdmin && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <input
                type="checkbox"
                checked={!!releaseDeposit}
                disabled={loading}
                onChange={(e) => onReleaseDepositChange && onReleaseDepositChange(e.target.checked)}
                data-cy="reservation-cancel-release-deposit"
              />
              <span>{t('calendar.delete.releaseDeposit')}</span>
            </label>
          )}
          {isAdmin && !releaseDeposit && <span style={{ ...drobny, marginTop: 8 }}>{t('calendar.delete.depositKeptHint')}</span>}
        </div>
      )}

      {/* Kaucja pobrana: zostaje przy rezerwacji, zwrot w karcie kaucji szczegółów rezerwacji. */}
      {m.depositCapturedGrosze > 0 && (
        <div style={blok} data-cy="reservation-cancel-deposit-captured">
          <div style={wiersz}>
            <span style={podpis}>{t('calendar.delete.depositCaptured')}</span>
            <strong>{zl(m.depositCapturedGrosze)}</strong>
          </div>
          {isAdmin && <span style={drobny}>{t('calendar.delete.depositCapturedHint')}</span>}
        </div>
      )}

      {/* #2: fetch płatności padł, nie wiemy, czy jest coś do zwrotu. Ostrzegamy zamiast po cichu usuwać. */}
      {refundableLoadFailed && (
        <div style={{ marginBottom: 16 }}><Banner tone="warning" compact>{t('calendar.delete.refundCheckFailed')}</Banner></div>
      )}

      {error && (
        <div style={{ marginTop: 12 }}><Banner tone="danger" compact>{error}</Banner></div>
      )}
    </Modal>
  );
};

export default DeleteConfirmationModal;
